import { create } from 'zustand'

import { type iNotificationsSettings } from 'types/iTypes'

type NotificationChannel =
    | 'push'
    | 'in_app'
    | 'toast'
    | 'sms'
    | 'email'

type GlobalNotificationKey =
    | 'push_notifications_enabled'
    | 'toast_notifications_enabled'
    | 'in_app_notifications_enabled'
    | 'sms_notifications_enabled'
    | 'email_notifications_enabled'

interface NotificationSettingsStore {
    settings: iNotificationsSettings | null

    profileSettings: Record<string, iNotificationsSettings>

    setSettings: (settings: iNotificationsSettings) => void

    setGlobalSetting: (
        key: GlobalNotificationKey,
        value: boolean
    ) => void

    setChannelSetting: (
        channel: NotificationChannel,
        key: string,
        value: boolean
    ) => void

    setProfileSettings: (
        userId: string,
        settings: iNotificationsSettings
    ) => void

    setProfileChannelSetting: (
        userId: string,
        channel: NotificationChannel,
        key: string,
        value: boolean
    ) => void

    clearSettings: () => void
}

export const useNotificationSettingsStore =
    create<NotificationSettingsStore>(set => ({
        settings: null,

        profileSettings: {},

        setSettings: settings => {
            set({ settings })
        },

        setGlobalSetting: (key, value) => {
            set(state => {
                if (!state.settings) {
                    return state
                }

                return {
                    settings: {
                        ...state.settings,
                        notificationSettings: {
                            ...state.settings.notificationSettings,
                            [key]: value
                        }
                    }
                }
            })
        },

        setChannelSetting: (channel, key, value) => {
            set(state => {
                if (!state.settings) {
                    return state
                }

                return {
                    settings: {
                        ...state.settings,
                        [channel]: {
                            ...state.settings[channel],
                            [key]: value
                        }
                    }
                }
            })
        },

        setProfileSettings: (userId, settings) => {
            set(state => ({
                profileSettings: {
                    ...state.profileSettings,
                    [userId]: settings
                }
            }))
        },

setProfileChannelSetting: (
    userId,
    channel,
    key,
    value
) => {
    set(state => {
        const profile =
            state.profileSettings[userId]

        if (!profile) {
            return state
        }

        const channelSettings =
            profile[channel] as
                | Record<string, boolean>
                | {
                      model?: Record<string, boolean>
                      subscriber?: Record<string, boolean>
                  }

        const isGrouped =
            !!channelSettings &&
            typeof channelSettings === 'object' &&
            (
                Object.prototype.hasOwnProperty.call(
                    channelSettings,
                    'model'
                ) ||
                Object.prototype.hasOwnProperty.call(
                    channelSettings,
                    'subscriber'
                )
            )

        return {
            profileSettings: {
                ...state.profileSettings,

                [userId]: {
                    ...profile,

                    [channel]: isGrouped
                        ? {
                            ...channelSettings,

                            subscriber: {
                                ...(
                                    (
                                        channelSettings as {
                                            subscriber?: Record<
                                                string,
                                                boolean
                                            >
                                        }
                                    ).subscriber ?? {}
                                ),

                                [key]: value
                            }
                        }
                        : {
                            ...channelSettings,
                            [key]: value
                        }
                }
            }
        }
    })
},

        clearSettings: () => {
            set({
                settings: null,
                profileSettings: {}
            })
        }
    }))